import React from 'react';

const ActressProfileDisplay = ({ profile }) => {
  if (!profile || (!profile.social && !profile.additionalInfo && !profile.dmm)) {
    return null;
  }

  const { social = {}, additionalInfo = {}, dmm = {} } = profile;

  const SocialLink = ({ platform, data, icon }) => {
    if (!data || !data.url) return null;
    
    return (
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
      >
        <span className="text-lg">{icon}</span>
        <span className="text-sm">
          {data.id || platform}
          {data.verified && <span className="ml-1 text-blue-500">✓</span>}
        </span>
      </a>
    );
  };

  const InfoItem = ({ label, value, isUrl = false }) => {
    if (!value) return null;
    
    return (
      <div className="mb-2">
        <span className="text-sm text-gray-600">{label}: </span>
        {isUrl ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            リンク
          </a>
        ) : (
          <span className="text-sm text-gray-900">{value}</span>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 mt-4">
      <h3 className="text-lg font-semibold mb-3">追加情報</h3>
      
      {/* DMM Information (if available) */}
      {dmm && Object.keys(dmm).length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">DMM情報</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <InfoItem label="名前（日本語）" value={dmm.japaneseName} />
            <InfoItem label="生年月日" value={dmm.birthdate ? new Date(dmm.birthdate).toLocaleDateString('ja-JP') : null} />
            <InfoItem label="身長" value={dmm.height ? `${dmm.height}cm` : null} />
            <InfoItem label="スリーサイズ" value={
              dmm.bust || dmm.waist || dmm.hip 
                ? `B${dmm.bust || '?'} W${dmm.waist || '?'} H${dmm.hip || '?'}` 
                : null
            } />
            <InfoItem label="血液型" value={dmm.bloodType ? `${dmm.bloodType}型` : null} />
            <InfoItem label="デビュー日" value={dmm.debutDate ? new Date(dmm.debutDate).toLocaleDateString('ja-JP') : null} />
          </div>
          {dmm.description && (
            <div className="mt-2">
              <span className="text-sm text-gray-600">説明: </span>
              <p className="text-sm text-gray-900 mt-1">{dmm.description}</p>
            </div>
          )}
        </div>
      )}
      
      {/* SNS Links */}
      {Object.keys(social).length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">SNS</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SocialLink platform="Twitter" data={social.twitter} icon="𝕏" />
            <SocialLink platform="Instagram" data={social.instagram} icon="📷" />
            <SocialLink platform="TikTok" data={social.tiktok} icon="🎵" />
            <SocialLink platform="YouTube" data={social.youtube} icon="▶️" />
          </div>
          
          {/* Paid content links */}
          {(social.onlyfans || social.fanbox || social.fantia) && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-2">有料コンテンツ</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {social.onlyfans && (
                  <a
                    href={social.onlyfans}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    OnlyFans
                  </a>
                )}
                {social.fanbox && (
                  <a
                    href={social.fanbox}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    pixivFANBOX
                  </a>
                )}
                {social.fantia && (
                  <a
                    href={social.fantia}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Fantia
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Additional Information */}
      {Object.keys(additionalInfo).length > 0 && (
        <div className="pt-3 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">その他の情報</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <InfoItem label="所属事務所" value={additionalInfo.agency} />
            <InfoItem label="出身地" value={additionalInfo.birthplace} />
            <InfoItem label="公式サイト" value={additionalInfo.officialWebsite} isUrl />
            <InfoItem label="ブログ" value={additionalInfo.blog} isUrl />
          </div>
          
          {additionalInfo.verifiedByAdmin && (
            <div className="mt-2 text-sm text-green-600 flex items-center">
              <span className="mr-1">✓</span>
              管理者確認済み
            </div>
          )}
        </div>
      )}
      
      {/* Aliases */}
      {profile.aliases && profile.aliases.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">別名・旧名</h4>
          <div className="flex flex-wrap gap-2">
            {profile.aliases.map((alias, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-200 text-sm rounded-md"
                title={alias.notes || alias.alias_type}
              >
                {alias.alias_name}
                {alias.alias_type === 'former' && ' (旧)'}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActressProfileDisplay;